import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import { Alert, Divider, Result } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'

import { getOrder, acceptConditions } from 'Redux/actions/app-actions'

import Loader from 'Components/Loader'
import OrderCard from 'Components/OrderCard'
import Button from 'Components/Button'
import Condition from 'Components/Condition'
import UnfairTerms from 'Components/UnfairTerms'
import { PageTitle, SmallTitle, Text } from 'Common/styled'

import { getQueryParam } from 'Common/utils'
import {
  device,
  colors,
  BU,
  ECOSIGN_STATUS_ACCEPTABLE_LIST,
  ECOSIGN_STATUS_ACCEPTED_LIST
} from 'Common/constants'

const { REACT_APP_API_URL } = process.env;

class Home extends Component {
  static propTypes = {
    acceptConditions: PropTypes.func,
    getOrder: PropTypes.func,
    order: PropTypes.object,
  }

  state = {
    isLoading: false,
    isConditionsAccepted: false,
    viewedConditions: {},
    acceptedConditions: {},
    isUnfairTermsAccepted: false,
  }

  componentDidMount() {
    this.getOrder()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.isConditionsAccepted && !this.state.isConditionsAccepted) this.getOrder()
  }

  getOrder = async () => {
    const { getOrder } = this.props
    const uuid = getQueryParam('uuid')
    if (uuid) {
      this.setState({ isLoading: true })
      const res = await getOrder(uuid)
      const newState = { isLoading: false }
      if (res.status === 202) newState.hasError = true
      this.setState(newState)
    } else {
      this.setState({ hasError: true })
    }
  }

  viewConditions = changedValues => {
    this.setState(prevState => ({ viewedConditions: { ...prevState.viewedConditions, ...changedValues } }))
  }

  changeConditions = changedValues => {
    this.setState(prevState => ({ acceptedConditions: { ...prevState.acceptedConditions, ...changedValues } }))
  }

  changeUnfairTerms = isAccepted => {
    this.setState({ isUnfairTermsAccepted: isAccepted })
  }

  acceptConditions = async () => {
    const { order, acceptConditions } = this.props
    const conditionUrls = order.conditionIds.map(c =>
      `${REACT_APP_API_URL}/customer/order/${order.id}/condition/${c}?bu=${BU}`
    )
    this.setState({ isLoading: true })
    const res = await acceptConditions(order.id, conditionUrls)
    const newState = { isLoading: false }
    if (res.status === 200) newState.isConditionsAccepted = true
    this.setState(newState)
  }

  renderAcceptedConditions() {
    const { order } = this.props
    return <>
      <Text>
        <FormattedMessage
          id={'home.text.acceptedConditions'}
          values={{
            customer: <strong>{order.customerFirstName}</strong>,
            br: <br />,
          }}
        />
      </Text>
      <SmallTitle><FormattedMessage id={'home.title.otpCode'} /></SmallTitle>
      <PageTitle>{order.otp}</PageTitle>
      <Button
        icon={<ArrowLeftOutlined />}
        type={'link'}
        style={{ justifySelf: 'center', color: colors.grey4 }}
        onClick={() => this.setState({ isConditionsAccepted: false })}
      >
        <span>
          <FormattedMessage id={'home.button.back'} />
        </span>
      </Button>
    </>
  }

  getConditionUrl = (conditionId) => {
    const { order } = this.props
    return `${REACT_APP_API_URL}/customer/order/${order.id}/condition/${conditionId}?bu=${BU}`
  }

  renderConditions() {
    const { viewedConditions, acceptedConditions } = this.state
    const { intl, order } = this.props
    const [sellConditions, ...attachments] = order.conditionIds ?? []
    const isConditionsViewed = order.conditionIds?.every(c => viewedConditions[c])
    const isSubmitEnabled =
      order.conditionIds?.every(c => acceptedConditions[c]) && this.state.isUnfairTermsAccepted
    const canAcceptConditions = ECOSIGN_STATUS_ACCEPTABLE_LIST.includes(order.ecoSignStatus)
    const areConditionsAccepted = ECOSIGN_STATUS_ACCEPTED_LIST.includes(order.ecoSignStatus)
    // Any other status (before sending, cancelled): show only the order summary
    if (!canAcceptConditions && !areConditionsAccepted) return null
    return (
      <>
        <Divider style={{ margin: '8px 0' }} />
        {areConditionsAccepted ?
          <Alert banner type={'success'} message={intl.formatMessage({ id: 'home.banner.conditionsAccepted' })} />
          : null}
        {canAcceptConditions && !isConditionsViewed ? (
          <Alert banner type={'warning'} message={intl.formatMessage({ id: 'home.banner.viewConditions' })} />
        ) : null}
        {attachments?.map((id, index) => {
          return (
            <Condition
              key={id}
              title={intl.formatMessage(
                { id: 'condition.button.attachment' },
                { index: index + 1 }
              )}
              id={id}
              url={this.getConditionUrl(id)}
              isEnabled={canAcceptConditions}
              onChange={this.changeConditions}
              onLinkClick={this.viewConditions}
            />
          )
        })}
        {sellConditions && <>
          <Condition
            id={sellConditions}
            title={intl.formatMessage({ id: 'condition.button.sellConditions' })}
            url={this.getConditionUrl(sellConditions)}
            isEnabled={canAcceptConditions}
            onChange={this.changeConditions}
            onLinkClick={this.viewConditions}
          />
          <UnfairTerms
            isEnabled={canAcceptConditions}
            onChange={this.changeUnfairTerms}
          />
        </>
        }
        {canAcceptConditions ? (
          <Button
            disabled={!isSubmitEnabled}
            onClick={this.acceptConditions}
            style={{ justifySelf: 'end' }}>
            <FormattedMessage id={'home.button.acceptConditions'} />
          </Button>
        ) : null}
      </>
    )
  }

  render() {
    const { isLoading, hasError, isConditionsAccepted } = this.state
    const { order } = this.props
    if (isLoading) return <Loader />
    if (hasError)
      return (
        <Result
          status={404}
          title={<FormattedMessage id={'home.text.hasError'} />}
        />
      )
    if (isConditionsAccepted) return this.renderAcceptedConditions()
    return (
      <>
        <Text>
          <FormattedMessage
            id={'home.text.greeting'}
            values={{
              customer: <strong>{order.customerFirstName}</strong>,
              br: <br />,
            }}
          />
        </Text>
        <OrderCard data={order} device={device} />
        {this.renderConditions()}
      </>
    )
  }
}

const mapStateToProps = state => ({
  order: state.app.order,
})
const mapDispatchToProps = {
  getOrder,
  acceptConditions
}
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Home))
